/**
 * Call Graph Diagram Data Model
 * Phase 2: AST Parsing for Repository Analysis
 *
 * Represents function call relationships, showing how functions
 * call each other throughout the codebase.
 */
import { z } from 'zod';
import { CodeEvidenceSchema } from './diagram.js';
/**
 * Type of function node
 */
export const FunctionNodeType = z.enum([
    'function', // Regular function
    'method', // Class method
    'arrow', // Arrow function
    'constructor', // Class constructor
    'entry', // Entry point (exported handlers)
    'external', // External library call
    'critical', // AI-flagged as important
]);
/**
 * A function node in the call graph
 */
export const CallGraphNodeSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Function name */
    name: z.string(),
    /** Qualified name (Class.method or module.function) */
    qualifiedName: z.string(),
    /** Module containing this function */
    module: z.string(),
    /** Type of function */
    type: FunctionNodeType,
    /** Whether the function is async */
    isAsync: z.boolean().default(false),
    /** Whether this is an entry point */
    isEntryPoint: z.boolean().default(false),
    /** Whether this is an external call */
    isExternal: z.boolean().default(false),
    /** AI-generated description of what this function does */
    description: z.string().optional(),
    /** Complexity metrics */
    complexity: z.object({
        cyclomatic: z.number(),
        branches: z.number(),
        loops: z.number(),
    }).optional(),
    /** Location in source */
    location: z.object({
        file: z.string(),
        startLine: z.number(),
        endLine: z.number(),
    }).optional(),
    /** Number of times this function is called */
    calledCount: z.number().default(0),
    /** Number of calls this function makes */
    callCount: z.number().default(0),
    /** Code evidence showing where this function is defined (Issue #177/#182) - DEPRECATED: use data.evidence instead */
    evidence: z.array(CodeEvidenceSchema).optional(),
    /** Arbitrary data for this node including evidence (Issue #223) */
    data: z.record(z.unknown()).optional(),
});
/**
 * Confidence level for edge matching (Issue #413)
 */
export const EdgeConfidenceLevel = z.enum([
    'high', // 1.0 - Exact qualified name match or same-file match
    'medium', // 0.7 - Cross-file name match with import verification
    'low', // 0.4 - Cross-file name match without import (uncertain)
    'uncertain', // 0.2 - Fallback name-only match (likely false positive)
]);
/**
 * Resolution method used to determine the call target (Issue #413)
 */
export const EdgeResolutionMethod = z.enum([
    'qualified', // Matched by full qualified name
    'sameFile', // Matched by name in same file
    'imported', // Matched by name with verified import
    'crossFile', // Matched by name across files (uncertain)
    'external', // External library call
]);
/**
 * A call edge in the call graph
 */
export const CallGraphEdgeSchema = z.object({
    /** Unique identifier */
    id: z.string(),
    /** Source function (caller) */
    source: z.string(),
    /** Target function (callee) */
    target: z.string(),
    /** Whether this is an async call */
    isAsync: z.boolean().default(false),
    /** Whether this is a method call */
    isMethodCall: z.boolean().default(false),
    /** Number of call sites */
    callCount: z.number().default(1),
    /** AI-generated label describing what the call does */
    label: z.string().optional(),
    /** Line numbers where calls occur */
    callSites: z.array(z.number()).default([]),
    /** Confidence level of this edge (Issue #413) */
    confidence: z.number().min(0).max(1).default(1.0),
    /** Confidence category for filtering (Issue #413) */
    confidenceLevel: EdgeConfidenceLevel.optional(),
    /** How this edge was resolved (Issue #413) */
    resolutionMethod: EdgeResolutionMethod.optional(),
    /** Code evidence showing call sites (Issue #197) - DEPRECATED: use data.evidence instead */
    evidence: z.array(CodeEvidenceSchema).optional(),
    /** Arbitrary data for this edge including evidence (Issue #223) */
    data: z.record(z.unknown()).optional(),
});
/**
 * A critical path through the call graph
 */
export const CriticalPathSchema = z.object({
    /** Path identifier */
    id: z.string(),
    /** Ordered list of function IDs in the path */
    path: z.array(z.string()),
    /** Description of what this path does */
    description: z.string().optional(),
    /** Type of critical path */
    type: z.enum(['database', 'http', 'auth', 'file', 'queue', 'other']).optional(),
    /** Whether this is the main/happy path */
    isPrimary: z.boolean().default(false),
});
/**
 * Complete Call Graph Diagram data
 */
export const CallGraphDataSchema = z.object({
    /** Diagram metadata */
    metadata: z.object({
        title: z.string(),
        description: z.string().optional(),
        diagramType: z.literal('callGraph'),
        c4Level: z.literal('code').default('code'),
        generatedAt: z.date().optional(),
        /** The entry point this graph was traced from */
        entryPoint: z.string().optional(),
        /** Maximum depth of the trace */
        maxDepth: z.number().optional(),
        /** Source files analyzed (Issue #177) */
        sourceFiles: z.array(z.string()).optional(),
        /** Code evidence showing where patterns were detected (Issue #177) */
        sourceEvidence: z.array(CodeEvidenceSchema).optional(),
    }),
    /** All function nodes */
    nodes: z.array(CallGraphNodeSchema),
    /** Call relationships */
    edges: z.array(CallGraphEdgeSchema),
    /** Entry points (starting nodes) */
    entryPoints: z.array(z.string()),
    /** Leaf functions (don't call others) */
    leafFunctions: z.array(z.string()),
    /** Critical paths identified */
    criticalPaths: z.array(CriticalPathSchema).default([]),
    /** Hotspot functions (called by many) */
    hotspots: z.array(z.object({
        id: z.string(),
        callerCount: z.number(),
    })).default([]),
    /** Potentially unused functions */
    unusedFunctions: z.array(z.string()).default([]),
    /** Statistics */
    stats: z.object({
        totalFunctions: z.number(),
        internalFunctions: z.number(),
        externalCalls: z.number(),
        avgCallsPerFunction: z.number(),
        maxCallDepth: z.number(),
    }).optional(),
});
/**
 * Create an empty Call Graph diagram
 */
export function createCallGraphDiagram(title, options) {
    return {
        metadata: {
            title,
            diagramType: 'callGraph',
            c4Level: 'code',
            ...options,
        },
        nodes: [],
        edges: [],
        entryPoints: [],
        leafFunctions: [],
        criticalPaths: [],
        hotspots: [],
        unusedFunctions: [],
    };
}
/**
 * Node styling based on type
 */
export const CALL_NODE_STYLES = {
    function: { bg: '#e3f2fd', border: '#1976d2' }, // Blue
    method: { bg: '#e8f5e9', border: '#388e3c' }, // Green
    arrow: { bg: '#f3e5f5', border: '#7b1fa2' }, // Purple
    constructor: { bg: '#fff8e1', border: '#ff8f00' }, // Amber
    entry: { bg: '#c8e6c9', border: '#2e7d32', icon: '▶' }, // Dark green with play icon
    external: { bg: '#ffebee', border: '#c62828', icon: '⬆' }, // Red with up arrow
    critical: { bg: '#ffcdd2', border: '#b71c1c', icon: '⚠' }, // Dark red with warning
};
//# sourceMappingURL=callGraph.js.map